'use client';

import { FC, useState, useEffect, useRef } from 'react';
import { useCompetitionContext } from "../../providers/CompetitionContext";
import { getCompetitionCopy } from "../../lib/competitionCopy";
import { useTeamContext } from '../../providers/TeamContext';
import { DriverCard } from "./driver-card";
import { Calendar } from "../../components/calendar";
import { LoadingSpinner } from "../../components/loading-spinner";
import TeamStats from '../../components/team-stats'; // Import the new component
import { Flag, Bus } from 'lucide-react';
import { chunkByRowSizes, generateBenchRowSizes, generatePyramidRowSizes } from './row-layout';

import moment from 'moment';
import 'moment/locale/fr';
moment.locale('fr');

interface PointsProps {
  weekId: number;
}

const Points: FC<PointsProps> = ({ weekId }) => {
  const { competitionConfig, competitionData } = useCompetitionContext();
  const copy = getCompetitionCopy(competitionConfig?.competitionFeed);
  const { team } = useTeamContext();

  const [teamNameLocal, setTeamNameLocal] = useState('');
  const benchContainerRef = useRef<HTMLDivElement | null>(null);
  const [isCompactBench, setIsCompactBench] = useState(false);

  useEffect(() => {
    setTeamNameLocal(team.teamName);
  }, [team.teamName]);

  useEffect(() => {
    const node = benchContainerRef.current;
    if (!node || typeof ResizeObserver === 'undefined') return;

    const updateCompactBench = (width: number) => {
      setIsCompactBench(width < 400);
    };

    updateCompactBench(node.getBoundingClientRect().width);

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      updateCompactBench(entry.contentRect.width);
    });

    observer.observe(node);

    return () => observer.disconnect();
  }, []);

  const lineupSize = competitionConfig.lineupSize;
  const benchSize = Number(competitionConfig?.benchSize || 0);
  const hasBench = benchSize > 0;
  const shouldVerticallyCenterStarting = lineupSize === 15 && !hasBench;

  const statsByPlayer = new Map(
    (competitionData?.stats || [])
      .filter((s: any) => s.w === weekId)
      .map((s: any) => [s.i, s] as const)
  );

  const enrichDriverWithStats = (driver: any) => ({
    ...driver,
    club: competitionData.clubs.find((club: any) => club.id === driver.clubId),
    stats: statsByPlayer.get(driver.id) || null,
  });

  const startingSlots = [
    ...team.starting.map(enrichDriverWithStats),
    ...Array(Math.max(lineupSize - team.starting.length, 0)).fill(undefined),
  ];

  const benchSlots = hasBench ? [
    ...team.bench.map(enrichDriverWithStats),
    ...Array(Math.max(benchSize - team.bench.length, 0)).fill(undefined),
  ] : [];

  const startingRowSizes = generatePyramidRowSizes(lineupSize);
  const benchRowSizes = hasBench ? generateBenchRowSizes(benchSize) : [];
  const startingRowChunks = chunkByRowSizes(startingSlots, startingRowSizes);
  const benchRowChunks = hasBench ? chunkByRowSizes(benchSlots, benchRowSizes) : [];
  const isCaptainBoosterEnabled =
    competitionConfig.captainBooster === true &&
    team.tripleCaptain != null &&
    Number(team.tripleCaptain) === weekId;
  
  // Get the teamId for the TeamStats component
  const teamIdForStats = typeof team.teamId === 'number' ? team.teamId : 
                        (team as any).id || null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
      {!competitionData && <LoadingSpinner />}
      
      {/* Left column */}
      <div className="">
        <div className={`bg-[rgb(0_61_96)]  text-white p-4 rounded-t-lg`}>
          <h1 className="font-extra">Points {teamNameLocal}</h1>
        </div>
        
        {/* Team Stats Component */}
        {teamIdForStats && (
          <TeamStats teamId={teamIdForStats} weekId={weekId} />
        )}

        <div className="rounded-b-lg pt-0">
          {/* Starting Lineup */}
          <div className="px-4 pt-4 pb-1 flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#003d60] text-white text-xs font-bold uppercase tracking-wide px-3 py-1">
              <Flag className="h-3.5 w-3.5" />
              Partants
            </span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>
          <div 
            className={`mx-auto relative w-full h-auto bg-no-repeat bg-top bg-contain rounded-lg overflow-hidden ${
              shouldVerticallyCenterStarting
                ? 'flex min-h-[46rem] flex-col justify-center py-6'
                : 'pt-2 pb-10'
            }`}
            style={{
              backgroundSize: "cover",
              backgroundImage: "linear-gradient(to bottom, rgba(0,61,96,0.15), rgba(0,61,96,0.3)), url('https://ocnc-fantasy.b-cdn.net/files/road.jpg')"
            }}
          >
            {startingRowChunks.map((row, rowIndex) => (
              <div 
                key={`starting-${rowIndex}`}
                className={`flex justify-center gap-2 ${rowIndex && 'my-2'}`}
              >
                {row.map((driver, colIndex) => (
                  <DriverCard
                    key={colIndex}
                    driver={driver}
                    showPoints={true}
                    hasCaptainBoosterEnabled={isCaptainBoosterEnabled}
                  />
                ))}
              </div>
            ))}
          </div>

          {hasBench && (
            <>
              <div className="px-4 pt-3 pb-1 flex items-center gap-2">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-[#0076a9] text-white text-xs font-bold uppercase tracking-wide px-3 py-1">
                  <Bus className="h-3.5 w-3.5" />
                  Remplaçants
                </span>
                <div className="flex-1 h-px bg-gray-200" />
              </div>
              <div 
                ref={benchContainerRef}
                className="
                  mx-auto relative
                  pt-2
                  pb-10
                  w-full max-w-[614px] 
                  h-auto
                  bg-no-repeat bg-top bg-contain
                  rounded-lg overflow-hidden
                "
                style={{
                  backgroundSize: "cover",
                  backgroundImage: "linear-gradient(to bottom, rgba(0,118,169,0.15), rgba(0,118,169,0.3)), url('https://ocnc-fantasy.b-cdn.net/files/road.jpg')"
                }}
              >
                {benchRowChunks.map((row, rowIndex) => (
                  <div 
                    key={`bench-${rowIndex}`}
                    className={`flex justify-center ${isCompactBench ? 'gap-1 px-1' : 'gap-2 px-2'} ${rowIndex ? 'my-2' : ''}`}
                  >
                    {row.map((driver, colIndex) => (
                      <DriverCard
                        key={colIndex}
                        driver={driver}
                        showPoints={true}
                        hasCaptainBoosterEnabled={isCaptainBoosterEnabled}
                        compact={isCompactBench}
                      />
                    ))}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
      
      {/* Right column: Player List and Calendar */}
      <div className="rounded-b-lg">
        <div className={`bg-[rgb(0_61_96)]  text-white p-4 rounded-t-lg`}>
          <h1 className="font-extra">{copy.raceLabelPluralTitle}</h1>
        </div>
        <div className='bg-white pt-0'>
          <div className="overflow-y-auto flex-1">
            <Calendar/>
          </div>
        </div>
        {/* <div className="w-full mx-auto p-0 ">
          <a href='https://www.12gobiking.nl/?utm_source=ad.nl&utm_medium=banner&utm_campaign=wielerspel_giro' target='_blank'>
            <img src='https://dpgnl.s3.eu-west-1.amazonaws.com/files/klas-banner-12go.jpg' alt='12go-banner' />
          </a>
        </div> */}
      </div>
    </div>
  );
};

export default Points;
